import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, X, ArrowDownToLine, CheckCircle2, Clock, Copy, ShoppingCart, Smartphone, QrCode, Lock } from 'lucide-react';

import { gbApi, gbFmt, gbError } from './gbApi';
import { gbAviso } from './GbNotice';
import { GbCoin } from './GbCoin';
import { GbStatement } from './GbStatement';

const STATUS = {
  PENDING: { t: 'na fila de entrega', c: 'text-amber-300', I: Clock },
  PROCESSING: { t: 'entregando agora', c: 'text-amber-300', I: Loader2 },
  FAILED: { t: 'tentando de novo', c: 'text-orange-300', I: Clock },
  STUCK: { t: 'em análise do suporte', c: 'text-rose-300', I: Clock },
  REFUNDED: { t: 'devolvido ao saldo', c: 'text-purple-300', I: Clock },
  DELIVERED: { t: 'creditado na sua conta KAKO', c: 'text-emerald-300', I: CheckCircle2 },
};

const Compra = ({ pedido, onClose, onCredited }) => {
  const [p, setP] = useState(pedido);
  const timer = useRef(null);

  useEffect(() => {
    const checar = async () => {
      try {
        const { data } = await gbApi.get(`/coins/order/${pedido.order_id}`);
        setP(data.order);
        if (data.order?.status === 'CREDITED') {
          gbAviso.ok(`${gbFmt(data.order.coins)} moedas amarelas na sua carteira!`);
          onCredited?.();
          onClose();
        }
      } catch (e) { /* segue tentando */ }
    };
    timer.current = setInterval(checar, 4000);
    return () => clearInterval(timer.current);
  }, [pedido.order_id, onClose, onCredited]);

  const copiar = () => {
    navigator.clipboard?.writeText(p.pix_copy_paste || '');
    gbAviso.ok('Código PIX copiado!');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" data-testid="gb-buy-modal">
      <div className="w-full max-w-sm rounded-3xl border border-amber-700/60 bg-[#140a20] p-5 text-center">
        <div className="flex items-center justify-between">
          <p className="text-xs font-black uppercase tracking-widest text-amber-400/80">
            {p.method === 'pix' ? 'pague com pix' : 'confirme no mb way'}
          </p>
          <button onClick={onClose} data-testid="gb-buy-close" className="rounded-full border border-purple-700/60 p-1.5 text-purple-200">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <p className="mt-3 font-mono text-2xl font-black text-amber-200">
          <GbCoin className="mr-1" />{gbFmt(p.coins)}
        </p>
        <p className="text-[11px] text-purple-300/70">
          {p.method === 'pix' ? `R$ ${Number(p.amount_brl || 0).toFixed(2).replace('.', ',')}`
            : `€ ${Number(p.amount_eur || 0).toFixed(2).replace('.', ',')}`}
          {' '}· cai direto na carteira do evento
        </p>

        {p.method === 'pix' ? (
          <>
            {p.pix_copy_paste && (
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(p.pix_copy_paste)}`}
                alt="QR Code PIX" data-testid="gb-buy-qr"
                className="mx-auto mt-4 w-44 rounded-2xl border border-purple-800/60 bg-white p-2" />
            )}
            <button onClick={copiar} data-testid="gb-buy-copy"
              className="mx-auto mt-3 flex items-center gap-2 rounded-full border border-amber-600/70 px-4 py-2 text-[11px] font-black text-amber-200">
              <Copy className="h-3.5 w-3.5" />COPIAR CÓDIGO PIX
            </button>
          </>
        ) : (
          <div className="mt-4 flex flex-col items-center gap-2 text-purple-200/80">
            <Smartphone className="h-10 w-10 text-amber-300" />
            <p className="text-xs">Abra o app <b>MB WAY</b> no número {p.phone} e confirme o pagamento.</p>
          </div>
        )}

        <p className="mt-4 flex items-center justify-center gap-2 text-[11px] font-bold text-amber-300/80" data-testid="gb-buy-waiting">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />esperando a confirmação do pagamento…
        </p>
        <p className="mt-1 text-[10px] text-purple-300/50">
          As moedas entram sozinhas assim que o pagamento é confirmado pelo servidor.
        </p>
      </div>
    </div>
  );
};

export const GbWallet = ({ onClose, onChange, config }) => {
  const [dados, setDados] = useState(null);
  const [valor, setValor] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [comprando, setComprando] = useState('');
  const [pedido, setPedido] = useState(null);
  const [aba, setAba] = useState('carteira');

  const load = useCallback(async () => {
    try {
      const { data } = await gbApi.get('/prizes?limit=30');
      setDados(data);
      setValor(String(data.coins_balance || ''));
    } catch (e) { gbAviso.erro(gbError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saldo = Number(dados?.coins_balance || 0);
  const pedidoValor = Math.floor(Number(valor) || 0);
  const resgateLiberado = dados?.withdrawals_enabled !== false;
  const podeResgatar = resgateLiberado && pedidoValor > 0 && pedidoValor <= saldo && !enviando;
  const pack = config?.coin_pack || { coins: 10000, price_brl: 6 };

  const resgatar = async () => {
    setEnviando(true);
    try {
      const { data } = await gbApi.post('/coins/withdraw', { coins: pedidoValor });
      const st = data?.withdraw?.status;
      gbAviso.ok(st === 'DELIVERED'
        ? `${gbFmt(pedidoValor)} moedas creditadas na sua conta KAKO!`
        : `Resgate de ${gbFmt(pedidoValor)} moedas na fila de entrega.`);
      await load();
      onChange?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      gbAviso.erro(d === 'insufficient_coins' ? 'Saldo de moedas insuficiente.'
        : d === 'withdraw_in_progress' ? 'Você já tem um resgate em andamento.'
          : d === 'withdrawals_disabled' ? 'Resgates temporariamente indisponíveis. Tente mais tarde.'
            : d === 'invalid_amount' ? 'Informe quantas moedas quer resgatar.'
              : gbError(e));
      await load();
    } finally { setEnviando(false); }
  };

  const comprar = async (method) => {
    setComprando(method);
    try {
      const { data } = await gbApi.post('/coins/buy', { method });
      setPedido(data.order);
    } catch (e) { gbAviso.erro(gbError(e)); } finally { setComprando(''); }
  };

  return (
    <div className="pb-10" data-testid="gb-wallet">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-black tracking-wide text-amber-300 md:text-lg">CARTEIRA DE MOEDAS</h2>
        {onClose && (
          <button onClick={onClose} data-testid="gb-wallet-close" className="rounded-full border border-purple-700/60 p-2 text-purple-200">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="mt-3 flex gap-1 rounded-full border border-purple-900/70 bg-black/40 p-1">
        {[{ id: 'carteira', t: 'SALDO E RESGATE' }, { id: 'extrato', t: 'EXTRATO DA CARTEIRA' }].map((a) => (
          <button key={a.id} onClick={() => setAba(a.id)} data-testid={`gb-wallet-tab-${a.id}`}
            className={`flex-1 rounded-full px-3 py-1.5 text-[11px] font-black transition-colors ${aba === a.id
              ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-[#1b0d02]' : 'text-purple-300/70'}`}>
            {a.t}
          </button>
        ))}
      </div>

      {aba === 'extrato' ? (
        <div className="mt-4">
          <GbStatement scope="coins" title="EXTRATO DA CARTEIRA DE MOEDAS AMARELAS"
            testid="gb-coins-statement" />
        </div>
      ) : !dados ? (
        <div className="flex items-center gap-2 py-10 text-purple-300/70"><Loader2 className="h-4 w-4 animate-spin" />carregando…</div>
      ) : (
        <>
          <div className="mt-4 overflow-hidden rounded-3xl border border-amber-700/50 bg-gradient-to-br from-amber-950/40 via-[#1a1028] to-black"
            data-testid="gb-wallet-balance">
            <div className="flex flex-wrap items-end justify-between gap-4 px-5 py-5">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-amber-400/80">saldo na carteira</p>
                <p className="mt-1 flex items-center gap-2 font-mono text-4xl font-black leading-none text-amber-200 drop-shadow-[0_0_18px_rgba(255,190,60,.35)]">
                  <GbCoin className="h-[0.9em]" />{gbFmt(saldo)}
                </p>
                <p className="mt-1.5 text-[11px] text-purple-200/60">
                  moedas amarelas do evento · use no PvP ou resgate para a conta KAKO
                </p>
              </div>
              <div className="flex gap-2">
                <div className="rounded-2xl border border-emerald-800/50 bg-emerald-950/25 px-3 py-2 text-center" data-testid="gb-wallet-delivered">
                  <p className="text-[9px] font-black uppercase tracking-widest text-emerald-400/70">resgatado</p>
                  <p className="font-mono text-lg font-black text-emerald-200">{gbFmt(dados.delivered)}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-3 grid gap-3 lg:grid-cols-2">

            <div className="rounded-3xl border border-amber-700/40 bg-gradient-to-br from-amber-950/25 to-black/50 p-4"
              data-testid="gb-wallet-buy">
            <p className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-amber-300">
              <ShoppingCart className="h-4 w-4" />comprar moedas para jogar
            </p>
            <p className="mt-1 text-xs text-purple-200/80">
              Pacote de <b className="text-amber-300"><GbCoin className="mr-1" />{gbFmt(pack.coins)}</b> moedas por{' '}
              <b className="text-amber-300">R$ {Number(pack.price_brl).toFixed(2).replace('.', ',')}</b> — vai para a
              sua carteira do evento (não para a conta KAKO) e serve para duelar no PvP.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {dados.pay_methods?.pix && (
                <button onClick={() => comprar('pix')} disabled={!!comprando} data-testid="gb-wallet-buy-pix"
                  className="flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 px-5 py-2.5 text-sm font-black text-[#04231b] disabled:opacity-40">
                  {comprando === 'pix' ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
                  PAGAR COM PIX
                </button>
              )}
              {dados.pay_methods?.mbway && (
                <button onClick={() => comprar('mbway')} disabled={!!comprando} data-testid="gb-wallet-buy-mbway"
                  className="flex items-center gap-2 rounded-full border border-cyan-600/70 px-5 py-2.5 text-sm font-black text-cyan-200 disabled:opacity-40">
                  {comprando === 'mbway' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Smartphone className="h-4 w-4" />}
                  PAGAR COM MB WAY
                </button>
              )}
              {!dados.pay_methods?.pix && !dados.pay_methods?.mbway && (
                <p className="text-[11px] font-bold text-rose-300" data-testid="gb-wallet-nopay">
                  Nenhuma forma de pagamento disponível para o seu país. Fale com o suporte.
                </p>
              )}
            </div>
          </div>

            <div className="rounded-3xl border border-purple-800/50 bg-gradient-to-br from-[#160e24] to-black/60 p-4">
            {!resgateLiberado && (
              <p className="mb-3 flex items-center gap-2 rounded-2xl border border-amber-600/60 bg-amber-950/30 px-3 py-2 text-[11px] font-bold text-amber-200"
                data-testid="gb-wallet-withdraw-off">
                <Lock className="h-4 w-4 flex-shrink-0" />
                Resgates temporariamente indisponíveis. Tente mais tarde.
              </p>
            )}
            <p className="text-xs text-purple-200/80">
              Resgate <b className="text-amber-300">sem mínimo</b>: você pode tirar todo o seu saldo. O crédito vai
              direto para a sua conta KAKO, entra na fila do servidor e é conferido antes de pagar.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input value={valor} onChange={(e) => setValor(e.target.value.replace(/\D/g, ''))}
                inputMode="numeric" data-testid="gb-wallet-input"
                className="w-40 rounded-full border border-purple-700/60 bg-black/50 px-4 py-2.5 font-mono text-sm font-black text-amber-200 outline-none focus:border-amber-500"
                placeholder="quantas moedas" />
              <button onClick={() => setValor(String(saldo))} data-testid="gb-wallet-max"
                className="rounded-full border border-purple-700/60 px-3 py-2 text-[11px] font-black text-purple-200">
                tudo
              </button>
              <button onClick={resgatar} disabled={!podeResgatar} data-testid="gb-wallet-withdraw"
                className="flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-6 py-2.5 text-sm font-black text-[#1b0d02] transition-transform hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100">
                {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowDownToLine className="h-4 w-4" />}
                RESGATAR
              </button>
            </div>
            {pedidoValor > saldo && <p className="mt-2 text-[11px] font-bold text-rose-300" data-testid="gb-wallet-error">Você não tem esse saldo.</p>}
            </div>
          </div>

          <h3 className="mt-6 text-xs font-black uppercase tracking-widest text-purple-300/70">meus resgates</h3>
          {(dados.withdrawals || []).length === 0 && (
            <p className="mt-2 text-xs text-purple-300/60" data-testid="gb-wallet-empty">Nenhum resgate pedido ainda.</p>
          )}
          <div className="mt-2 grid gap-2 md:grid-cols-2">
            {(dados.withdrawals || []).map((w) => {
              const s = STATUS[w.status] || STATUS.PENDING;
              return (
                <div key={w.withdraw_id} data-testid={`gb-wallet-item-${w.withdraw_id}`}
                  className="flex items-center justify-between rounded-2xl border border-purple-800/50 bg-black/30 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="font-mono text-sm font-black text-amber-200"><GbCoin className="mr-1" />{gbFmt(w.coins)}</p>
                    <p className="text-[10px] text-purple-300/50">{new Date(w.created_at).toLocaleString('pt-BR')}</p>
                  </div>
                  <span className={`flex items-center gap-1.5 text-[11px] font-bold ${s.c}`}>
                    <s.I className="h-3.5 w-3.5" />{s.t}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {pedido && (
        <Compra pedido={pedido} onClose={() => setPedido(null)}
          onCredited={async () => { await load(); onChange?.(); }} />
      )}
    </div>
  );
};
